/**
 * The application shell: the rail, the top bar, the content.
 *
 * Laid out after the reference design the product owner chose (2026-09-24): a
 * full-height dark rail on the start edge carrying the property, the
 * navigation and the user, and a slim translucent top bar over the content
 * carrying the breadcrumb and the live chips. The whole signed-in application
 * sits on `.ground`, the two faint washes of light in styles/tokens.css.
 *
 * Two rules from docs/08-uiux.md §3 are enforced structurally, in the rail:
 *
 * - A sidebar section is **hidden entirely** when the user has no permission
 *   for anything inside it. An auditor sees Dashboard, Archive, Reports and
 *   Audit, and nothing else — no greyed-out items teasing capabilities they
 *   do not have.
 * - Navigation is at most two levels deep. Anything that wants a third is a
 *   tab inside a detail page.
 */

import { useEffect, useState, type ReactNode } from 'react'
import { PanelLeftClose, PanelLeftOpen } from 'lucide-react'
import { Link, useRouter } from '@/app/router'
import { landingPath, locate, visibleSections } from '@/app/navigation'
import { Rail } from '@/app/Rail'
import { useAuth } from '@/lib/auth'
import { onConnectionChange, type ConnectionState } from '@/lib/socket'
import { Badge, StatusDot } from '@/components/ui/primitives'
import { ConnectionBanner } from '@/components/ui/states'
import { cn } from '@/lib/utils'
import { useDocumentTitle } from '@/lib/branding'
import { HotelLogo } from '@/components/domain/HotelLogo'
import { DeskChip } from '@/features/desks/DeskChip'
import { NotificationBell } from '@/features/notifications/NotificationBell'
import { ThemeToggle } from '@/app/ThemeToggle'

/** Remembered per browser: a convenience, not a setting. */
const RAIL_STORAGE_KEY = 'ereg-rail'

function useWide(): boolean {
  const query = '(min-width: 1024px)'
  const [wide, setWide] = useState(() => window.matchMedia(query).matches)
  useEffect(() => {
    const media = window.matchMedia(query)
    const onChange = () => setWide(media.matches)
    media.addEventListener('change', onChange)
    return () => media.removeEventListener('change', onChange)
  }, [])
  return wide
}

function useRailPreference(): [boolean, () => void] {
  const [collapsed, setCollapsed] = useState(() => {
    try {
      return localStorage.getItem(RAIL_STORAGE_KEY) === 'collapsed'
    } catch {
      return false
    }
  })
  const toggle = () =>
    setCollapsed((value) => {
      try {
        localStorage.setItem(RAIL_STORAGE_KEY, value ? 'expanded' : 'collapsed')
      } catch {
        // Not remembered, but it still toggles for this visit.
      }
      return !value
    })
  return [collapsed, toggle]
}

export function Shell({ children }: { children: ReactNode }) {
  const { can, canAny, session } = useAuth()
  const { path } = useRouter()
  const property = session?.property ?? null
  useDocumentTitle(property?.name ?? null)
  const [connection, setConnection] = useState<ConnectionState>('connecting')
  const [preferCollapsed, toggleRail] = useRailPreference()
  const wide = useWide()

  useEffect(() => onConnectionChange(setConnection), [])

  const visible = visibleSections(can, canAny)
  const home = landingPath(can, canAny)
  // Below 1024px the rail is an icon strip whatever was chosen: the content
  // needs the width more than the labels do.
  const collapsed = !wide || preferCollapsed

  return (
    <div className="ground flex min-h-screen bg-[var(--bg)]">
      <Rail sections={visible} home={home} collapsed={collapsed} />

      <div className="flex min-w-0 flex-1 flex-col">
        <ConnectionBanner state={connection} />

        <header className="topbar sticky top-0 z-30 flex h-14 items-center gap-3 px-4 lg:px-6">
          {wide && (
            <button
              type="button"
              onClick={toggleRail}
              aria-label={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
              title={collapsed ? 'Expand the sidebar' : 'Collapse the sidebar'}
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-[var(--border-strong)] text-[var(--text-muted)] transition-colors hover:bg-[var(--surface-2)] hover:text-[var(--text)]"
            >
              {collapsed ? (
                <PanelLeftOpen className="h-4 w-4 flip-rtl" aria-hidden />
              ) : (
                <PanelLeftClose className="h-4 w-4 flip-rtl" aria-hidden />
              )}
            </button>
          )}

          <Breadcrumb path={path} />

          <div className="ms-auto flex items-center gap-2">
            <DeskChip />
            <ThemeToggle className="hidden sm:inline-flex" />
            <NotificationBell />
          </div>
        </header>

        {/* Desk View is a document on a screen, not a page of content: from xl
            its padding is trimmed so the page being checked gets the room. */}
        <main
          id="main-content"
          className={cn(
            'min-w-0 flex-1 px-5 py-6 lg:px-8 lg:py-7',
            path.startsWith('/workspace') && 'xl:px-3 xl:py-3',
          )}
        >
          {children}
        </main>
      </div>

      {/* Below 768px the admin surface is deliberately not supported: a
          phone-sized user-management table is worse than no table. The kiosk
          is a different URL and a different build. */}
      <div className="ground fixed inset-0 z-50 grid place-items-center bg-[var(--bg)] px-8 text-center md:hidden">
        <div>
          {property?.logo_url ? (
            <HotelLogo
              src={property.logo_url}
              surface="theme"
              className="mx-auto h-10 w-auto max-w-[14rem] object-center rtl:object-center"
            />
          ) : (
            <Badge tone="brand">{property?.name || 'EREG'}</Badge>
          )}
          <h1 className="mt-3 text-lg font-semibold">Please use a larger screen</h1>
          <p className="mt-2 text-[13px] text-[var(--text-muted)]">
            The reception and admin screens need at least a tablet in landscape. The guest kiosk is
            at <span className="font-mono">/kiosk</span>.
          </p>
          <div className="mt-4 flex justify-center">
            <StatusDot
              status={connection === 'connected' ? 'online' : 'offline'}
              label={connection}
            />
          </div>
        </div>
      </div>
    </div>
  )
}

/**
 * "Workspace / Desk View": the section and the screen, from the same lookup
 * that picks the rail's current item. Below the screen's own route — a stay,
 * a template version — the screen's name links back up to it.
 */
function Breadcrumb({ path }: { path: string }) {
  const here = locate(path)
  if (!here) return null
  const deeper = path !== here.item.to

  return (
    <nav aria-label="Breadcrumb" className="hidden min-w-0 sm:block">
      <ol className="flex items-center gap-2 text-[13px]">
        <li className="text-[var(--text-muted)]">{here.section.label}</li>
        <li className="text-[var(--text-muted)]" aria-hidden>
          /
        </li>
        <li className="min-w-0 truncate font-medium text-[var(--text)]">
          {deeper ? (
            <Link to={here.item.to} className="hover:text-[var(--brand)]">
              {here.item.label}
            </Link>
          ) : (
            <span aria-current="page">{here.item.label}</span>
          )}
        </li>
      </ol>
    </nav>
  )
}
